// Session controls compartidos entre kine y paciente: toggle de rol +
// botón Cerrar sesión.
//
// B8: el logout pasó de "limpiar PasswordGate + reload" a
// `useAuth().signOut() + navigate('/login')`. AuthGate detecta status
// 'anonymous' post-signOut y mantiene al usuario en /login.
//
// Sprint 3 demo mode (5/7/2026): con `isDemoMode() === true`, el toggle
// hace SWAP REAL de sesión (signOut + supabase.auth.setSession con el
// snapshot del otro rol). Fuera de demo mode, el componente entero es
// invisible (return null al top) — el rol viene del JWT y no hay
// motivo de swap manual en producción normal.
//
// Setup del demo per-device: ver docs/decisions/demo-mode.md.
//
// Mobile (< md): solo íconos para no ocupar ancho.
// Desktop (>= md): íconos + labels.

import { useState } from "react";
import { useNavigate } from "react-router";
import { LogOut, Loader2, Stethoscope, User } from "lucide-react";
import { useAuth } from "./auth/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { isDemoMode } from "@/lib/demo/demoMode";
import { getStoredSession } from "@/lib/demo/sessionSnapshots";

const STORAGE_KEY = "gonix_active_role";

export type ActiveRole = "kine" | "paciente";

/** Lee el rol activo de localStorage. Default "kine" si no hay valor
 *  o es inválido. Exportado por si otros consumidores quieren leer
 *  el rol activo (no hace falta hoy, pero queda preparado). */
export function getActiveRole(): ActiveRole {
  if (typeof window === "undefined") return "kine";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "paciente" ? "paciente" : "kine";
}

function setActiveRole(role: ActiveRole) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, role);
}

export function RoleToggle() {
  const navigate = useNavigate();
  const { signOut, userRole } = useAuth();
  const [swapping, setSwapping] = useState(false);

  // Gate del PILL de swap: solo aparece en demo mode. Fuera de demo,
  // solo queda el botón LogOut visible (session controls compartidos —
  // no queremos perder el logout en prod normal).
  const showSwap = isDemoMode();

  // El `active` prioriza `userRole` del auth (fuente de verdad del rol
  // real) sobre localStorage. El pill activo refleja el rol real del
  // user logueado en cada momento.
  const active: ActiveRole = userRole ?? getActiveRole();

  const swapTo = async (targetRole: ActiveRole) => {
    if (targetRole === active) return;
    if (swapping) return;

    const stored = getStoredSession(targetRole);
    if (!stored) {
      // Setup incompleto — la otra cuenta no fue logueada aún en este
      // device. UX: alert simple explicando qué hacer.
      window.alert(
        `Demo: no hay sesión capturada para "${targetRole}". ` +
          `Logueate primero con esa cuenta en este device (mismo browser) ` +
          `y volvé al toggle.`,
      );
      return;
    }

    setSwapping(true);
    // Persist el rol elegido para que la UI post-swap arranque coherente
    // aunque el userRole del auth tarde un tick en resolverse.
    setActiveRole(targetRole);

    try {
      // setSession REEMPLAZA atómicamente la sesión activa por el
      // snapshot del otro rol — sin gap anonymous en medio, sin flash
      // a /login. Antes había un signOut() previo que abría una ventana
      // de status='anonymous' durante la cual AuthGate rebotaba a
      // /login (verificado contra AuthGate.tsx:48-58). El fix es que
      // setSession dispara onAuthStateChange con la nueva sesión
      // directamente — el AuthProvider ve session=A → session=B sin
      // pasar por session=null, y AuthGate NO redirige mientras
      // userRole se re-resuelve (línea 66-67 del AuthGate: userRole
      // undefined durante authenticated no dispara redirect).
      //
      // Supabase auto-refreshea el access_token si expiró (usando el
      // refresh_token del snapshot). Si el refresh_token también
      // expiró (~30 días default), el setSession falla y el user
      // queda en la sesión anterior — el error se muestra al usuario
      // sin ruptura del contexto.
      const { error } = await supabase.auth.setSession({
        access_token: stored.access_token,
        refresh_token: stored.refresh_token,
      });

      if (error) {
        window.alert(
          `Demo: no se pudo restaurar la sesión de "${targetRole}". ` +
            `El token puede haber expirado — logueate de nuevo con esa cuenta.`,
        );
        return;
      }

      // AuthProvider listener detecta el nuevo session → resuelve rol
      // → dispara re-render. Navegamos al home del rol nuevo para que
      // el user aterrice donde corresponde.
      navigate(targetRole === "kine" ? "/" : "/paciente-home");
    } finally {
      setSwapping(false);
    }
  };

  // Logout cierra sesión Supabase + navega a /login. AuthGate detecta
  // status='anonymous' y bloquea cualquier ruta privada que el usuario
  // intente. Full reload no es necesario — el AuthProvider listener
  // resetea el estado del contexto al detectar la sesión cerrada.
  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <div className="inline-flex items-center gap-1.5 shrink-0">
      {showSwap && (
        <div
          role="group"
          aria-label="Cambiar rol activo (demo)"
          className="inline-flex items-center bg-muted rounded-full p-0.5 text-xs font-semibold"
        >
          <button
            type="button"
            onClick={() => swapTo("kine")}
            aria-pressed={active === "kine"}
            disabled={swapping}
            className={`rounded-full px-2.5 md:px-3 py-1.5 transition-colors flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed ${
              active === "kine"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {swapping && active !== "kine" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
            ) : (
              <Stethoscope className="h-3.5 w-3.5" strokeWidth={2} />
            )}
            <span className="hidden md:inline">Kine</span>
          </button>
          <button
            type="button"
            onClick={() => swapTo("paciente")}
            aria-pressed={active === "paciente"}
            disabled={swapping}
            className={`rounded-full px-2.5 md:px-3 py-1.5 transition-colors flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed ${
              active === "paciente"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {swapping && active !== "paciente" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
            ) : (
              <User className="h-3.5 w-3.5" strokeWidth={2} />
            )}
            <span className="hidden md:inline">Paciente</span>
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={handleLogout}
        aria-label="Cerrar sesión"
        title="Cerrar sesión"
        className="rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        <LogOut className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}
